import apiClient from "@services/api/apiClient";
import API from "@config/shared/api-paths";

/**
 * Creates the preference actions for the store
 * @param {Function} set - Zustand set function
 * @param {Function} get - Zustand get function
 * @returns {Object} - Preference actions
 */
export const createPreferenceActions = (set, get) => ({
  /**
   * Get a preference value
   * @param {string} key - Preference key
   * @param {any} defaultValue - Default value if preference not found
   * @returns {Promise<any>} - The preference value
   */
  getPreference: async (key, defaultValue = null) => {
    const state = get();

    // Return cached value if available
    if (key in state.preferences) {
      return state.preferences[key];
    }

    // Check if already loading
    if (state.loading[key]) {
      // Wait for the existing request
      return new Promise((resolve) => {
        const checkLoaded = setInterval(() => {
          const currentState = get();
          if (!currentState.loading[key]) {
            clearInterval(checkLoaded);
            resolve(currentState.preferences[key] ?? defaultValue);
          }
        }, 50);
      });
    }

    // Set loading state
    set((state) => ({
      loading: { ...state.loading, [key]: true },
    }));

    try {
      const { data } = await apiClient.get(
        API.FRONTEND.USER_PREFERENCES + `/${key}`,
      );
      const value = data.value;

      // Update cache
      set((state) => ({
        preferences: { ...state.preferences, [key]: value },
        loading: { ...state.loading, [key]: false },
        errors: { ...state.errors, [key]: null },
      }));

      return value;
    } catch (error) {
      // 404 means preference not set, use default
      if (error.status === 404) {
        set((state) => ({
          preferences: { ...state.preferences, [key]: defaultValue },
          loading: { ...state.loading, [key]: false },
          errors: { ...state.errors, [key]: null },
        }));
        return defaultValue;
      }

      // Other errors
      set((state) => ({
        loading: { ...state.loading, [key]: false },
        errors: { ...state.errors, [key]: error.message },
      }));

      console.error(`Failed to fetch preference "${key}":`, error);
      return defaultValue;
    }
  },

  /**
   * Set a preference value (with optimistic update)
   * @param {string} key - Preference key
   * @param {any} value - New preference value
   * @param {Object} options - Optional configuration
   * @param {boolean} options.optimistic - Whether to update optimistically (default: true)
   * @returns {Promise<any>} - Returns the stored value or throws error
   */
  setPreference: async (key, value, options) => {
    const { optimistic = true } = options ?? {};
    const previousValue = get().preferences[key];

    // Optimistic update
    if (optimistic) {
      set((state) => ({
        preferences: { ...state.preferences, [key]: value },
      }));
    }

    try {
      const { data } = await apiClient.put(
        API.FRONTEND.USER_PREFERENCES + `/${key}`,
        { value },
      );

      // Confirm update with server response
      set((state) => ({
        preferences: { ...state.preferences, [key]: data.value },
        errors: { ...state.errors, [key]: null },
      }));

      return data;
    } catch (error) {
      // Rollback on error if optimistic
      if (options.optimistic) {
        set((state) => ({
          preferences: { ...state.preferences, [key]: previousValue },
          errors: { ...state.errors, [key]: error.message },
        }));
      }

      console.error(`Failed to update preference "${key}":`, error);
      throw error;
    }
  },

  /**
   * Batch load multiple preferences at once
   * @param {string[]} keys - Array of preference keys
   * @returns {Promise<Object>} - Map of key to value
   */
  loadPreferences: async (keys) => {
    const state = get();
    const uncachedKeys = keys.filter((key) => !(key in state.preferences));

    if (uncachedKeys.length === 0) {
      // All cached, return immediately
      const result = {};
      keys.forEach((key) => {
        result[key] = state.preferences[key];
      });
      return result;
    }

    // Load uncached preferences in parallel
    const results = await Promise.allSettled(
      uncachedKeys.map((key) => get().getPreference(key)),
    );

    const loadedPreferences = {};
    uncachedKeys.forEach((key, index) => {
      const result = results[index];
      loadedPreferences[key] =
        result.status === "fulfilled" ? result.value : null;
    });

    // Include already cached values
    keys.forEach((key) => {
      if (!(key in loadedPreferences)) {
        loadedPreferences[key] = state.preferences[key];
      }
    });

    return loadedPreferences;
  },
});
